import { db } from './database';

export type GamutClampMode = 'none' | 'srgb' | 'p3' | 'rec2020' | 'auto';

export interface PrecisionSettings {
  decimalPlaces: number;
  cmykStep: number;
  gamutClampMode: GamutClampMode;
}

export interface PrecisionPreset {
  id: string;
  name: string;
  settings: PrecisionSettings;
  isBuiltin?: boolean;
}

export interface PrecisionState {
  settings: PrecisionSettings;
  presets: PrecisionPreset[];
  activePresetId: string | null;
}

interface StoredPrecisionState {
  settings?: Partial<PrecisionSettings>;
  activePresetId?: string | null;
  customPresets?: PrecisionPreset[];
}

const clampNumber = (value: number, min: number, max: number) => {
  if (!Number.isFinite(value)) {
    return min;
  }
  if (value < min) {
    return min;
  }
  if (value > max) {
    return max;
  }
  return value;
};

const sanitizeName = (name: string) => {
  const trimmed = name.trim();
  return trimmed.length > 0 ? trimmed : 'Untitled preset';
};

const cloneSettings = (settings: PrecisionSettings): PrecisionSettings => ({
  decimalPlaces: settings.decimalPlaces,
  cmykStep: settings.cmykStep,
  gamutClampMode: settings.gamutClampMode,
});

export const DEFAULT_PRECISION_SETTINGS: PrecisionSettings = {
  decimalPlaces: 3,
  cmykStep: 0.5,
  gamutClampMode: 'auto',
};

export const BUILTIN_PRESETS: PrecisionPreset[] = [
  {
    id: 'builtin-screen-balanced',
    name: 'Screen balanced',
    settings: cloneSettings({
      decimalPlaces: 3,
      cmykStep: 0.5,
      gamutClampMode: 'auto',
    }),
    isBuiltin: true,
  },
  {
    id: 'builtin-print-focused',
    name: 'Print (CMYK emphasis)',
    settings: cloneSettings({
      decimalPlaces: 1,
      cmykStep: 1,
      gamutClampMode: 'srgb',
    }),
    isBuiltin: true,
  },
  {
    id: 'builtin-developer-raw',
    name: 'Developer (raw data)',
    settings: cloneSettings({
      decimalPlaces: 4,
      cmykStep: 0.25,
      gamutClampMode: 'none',
    }),
    isBuiltin: true,
  },
];

const builtinPresetMap = new Map<string, PrecisionPreset>(
  BUILTIN_PRESETS.map((preset) => [preset.id, preset]),
);

export const normalizePrecisionSettings = (
  settings?: Partial<PrecisionSettings>,
): PrecisionSettings => {
  const base: PrecisionSettings = {
    ...DEFAULT_PRECISION_SETTINGS,
  };

  if (!settings) {
    return cloneSettings(base);
  }

  const clampMode: GamutClampMode = (() => {
    switch (settings.gamutClampMode) {
      case 'none':
      case 'srgb':
      case 'p3':
      case 'rec2020':
      case 'auto':
        return settings.gamutClampMode;
      default:
        return base.gamutClampMode;
    }
  })();

  return {
    decimalPlaces: Math.round(clampNumber(settings.decimalPlaces ?? base.decimalPlaces, 0, 6)),
    cmykStep: clampNumber(settings.cmykStep ?? base.cmykStep, 0.05, 10),
    gamutClampMode: clampMode,
  };
};

const normalizePrecisionPreset = (preset: PrecisionPreset): PrecisionPreset => ({
  id: preset.id,
  name: sanitizeName(preset.name),
  isBuiltin: preset.isBuiltin,
  settings: normalizePrecisionSettings(preset.settings),
});

const mergePresets = (customPresets: PrecisionPreset[]): PrecisionPreset[] => {
  const merged = new Map<string, PrecisionPreset>();

  for (const preset of BUILTIN_PRESETS) {
    merged.set(preset.id, normalizePrecisionPreset(preset));
  }

  for (const preset of customPresets) {
    if (!preset.id) {
      continue;
    }
    merged.set(preset.id, normalizePrecisionPreset({ ...preset, isBuiltin: false }));
  }

  return Array.from(merged.values());
};

const extractCustomPresets = (presets: PrecisionPreset[]): PrecisionPreset[] =>
  presets
    .filter((preset) => !preset.isBuiltin)
    .map((preset) => ({
      id: preset.id,
      name: preset.name,
      settings: cloneSettings(preset.settings),
    }));

const sanitizeState = (state: PrecisionState): PrecisionState => {
  const normalizedSettings = normalizePrecisionSettings(state.settings);
  const mergedPresets = mergePresets(state.presets);

  let activePresetId = state.activePresetId;
  if (activePresetId && !mergedPresets.some((preset) => preset.id === activePresetId)) {
    activePresetId = null;
  }

  return {
    settings: cloneSettings(normalizedSettings),
    presets: mergedPresets,
    activePresetId,
  };
};

export const loadPrecisionState = async (): Promise<PrecisionState> => {
  const user = await db.getUserSettings();

  const stored = (user.settings?.precisionControls ?? {}) as StoredPrecisionState;

  const storedSettings = normalizePrecisionSettings({
    decimalPlaces: user.settings?.precision,
    ...stored.settings,
  });

  const customPresets = Array.isArray(stored.customPresets) ? stored.customPresets : [];

  const mergedPresets = mergePresets(customPresets);

  let activePresetId = stored.activePresetId ?? null;
  if (activePresetId && !mergedPresets.some((preset) => preset.id === activePresetId)) {
    activePresetId = null;
  }

  if (activePresetId) {
    const preset = mergedPresets.find((item) => item.id === activePresetId);
    if (preset) {
      return sanitizeState({
        settings: cloneSettings(preset.settings),
        presets: mergedPresets,
        activePresetId,
      });
    }
  }

  return sanitizeState({
    settings: storedSettings,
    presets: mergedPresets,
    activePresetId,
  });
};

export const persistPrecisionState = async (state: PrecisionState): Promise<void> => {
  const sanitized = sanitizeState(state);
  const payload: StoredPrecisionState = {
    settings: sanitized.settings,
    activePresetId: sanitized.activePresetId,
    customPresets: extractCustomPresets(sanitized.presets),
  };

  await db.updateUserSettings({
    precision: sanitized.settings.decimalPlaces,
    precisionControls: payload,
  });
};

export const resolvePresetById = (
  presets: PrecisionPreset[],
  presetId: string | null,
): PrecisionPreset | null => {
  if (!presetId) {
    return null;
  }
  return presets.find((preset) => preset.id === presetId) ?? builtinPresetMap.get(presetId) ?? null;
};

export const applyPresetToState = (
  state: PrecisionState,
  preset: PrecisionPreset,
): PrecisionState =>
  sanitizeState({
    settings: cloneSettings(preset.settings),
    presets: state.presets,
    activePresetId: preset.id,
  });
